import { Link } from "react-router-dom";
import TextPageNavigation from "../components/TextPageNavigation";
import { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="text-page">
      <div className="container">
        <div className="flex">
          <TextPageNavigation activePage="privacy" />
          <div className="text-page-content">
            <div>
              <h1>BackToReality's Privacy Policy</h1>
              <p>
                This Privacy Policy explains how BackToReality ("
                <strong>we</strong>," "<strong>us</strong>," or "
                <strong>our</strong>") collects, uses, and discloses information
                about you when you use our services. We are committed to
                protecting your privacy and handling your data responsibly.
              </p>
            </div>
            <div className="text-section">
              <h2>What Information We Collect</h2>
              <p>
                We collect information to provide and improve our services to
                you. This includes:
              </p>
              <ul>
                <li>
                  <strong>Information you provide directly:</strong> This
                  includes details you submit when you create an account,
                  participate in assessments, or contact us.{" "}
                </li>
                <li>
                  <strong>Information collected automatically:</strong> When you
                  use our services, we may automatically collect certain
                  information, such as your IP address, device type, browser
                  information, and how you interact with our website (e.g.,
                  pages visited, time spent).
                </li>
                <li>
                  <strong>Information from third parties:</strong> If you access
                  our services through a third-party platform (like social media
                  logins), they may provide us with certain information about
                  you, subject to their privacy policies.
                </li>
              </ul>
            </div>
            <div className="text-section">
              <h2>How We Use Your Information</h2>
              <p>
                We use the information we collect for various purposes,
                including:
              </p>
              <ul>
                <li>
                  <strong>Providing and improving our services:</strong> To
                  operate, maintain, and enhance the features and functionality
                  of BackToReality, including delivering personalized content
                  and insights.
                </li>
                <li>
                  <strong>Communication:</strong> To send you important updates,
                  notifications, and respond to your inquiries.
                </li>
                <li>
                  <strong>Research and analysis:</strong> To understand user
                  behavior, improve our assessments, and develop new features.
                  This often involves using aggregated and anonymized data.
                </li>
                <li>
                  <strong>Security and fraud prevention:</strong> To protect our
                  services and users from fraudulent or unauthorized activities.
                </li>
                <li>
                  <strong>Legal compliance:</strong> To comply with applicable
                  laws and regulations.
                </li>
              </ul>
            </div>
            <div className="text-section">
              <h2>How We Share Your Information</h2>
              <p>
                We understand the importance of keeping your information
                private. We will not sell your personal data to third parties.
                We may share your information in the following limited
                circumstances:
              </p>
              <ul>
                <li>
                  <strong>With your consent:</strong> We may share your
                  information if you give us explicit permission to do so
                </li>
                <li>
                  <strong>Service providers:</strong> We may share information
                  with third-party vendors who perform services on our behalf,
                  such as hosting, data analysis, and customer support. These
                  providers are obligated to protect your information and use it
                  only for the purposes we specify.
                </li>
              </ul>
              <p>
                Here’s a list of third party providers we’ll share your
                information with, if necessary:
              </p>
              <ol>
                <li>
                  MailChimp, to deliver our newsletters. We also gather
                  statistics around email opening and clicks to help us monitor
                  and improve our newsletters. MailChimp’s privacy policy is
                  here{" "}
                  <Link
                    to={"https://www.intuit.com/privacy/statement/"}
                    target="_blank"
                  >
                    https://www.intuit.com/privacy/statement/
                  </Link>
                  .
                </li>
                <li>
                  Microsoft, to deliver our non-newsletter e-mails, e.g., when
                  we respond to your messages. You can find Microsoft’s privacy
                  policy here{" "}
                  <Link
                    to={
                      "https://www.microsoft.com/en-gb/privacy/privacystatement"
                    }
                    target="_blank"
                  >
                    https://www.microsoft.com/en-gb/privacy/privacystatement
                  </Link>
                  .
                </li>
                <li>
                  Stripe and PayPal, to process payments and conduct anti-fraud
                  checks. Here are links to Stripe’s and PayPal’s privacy
                  policies:{" "}
                  <Link
                    to={"https://stripe.com/gb/privacy"}
                    target="_blank"
                  >
                    https://stripe.com/gb/privacy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to={
                      "https://www.paypal.com/lu/legalhub/paypal/privacy-full"
                    }
                    target="_blank"
                  >
                    https://www.paypal.com/lu/legalhub/paypal/privacy-full
                  </Link>
                  .
                </li>
                <li>
                  Google, to analyze the behaviour of our visitors. Google’s
                  privacy policy is here{" "}
                  <Link
                    to={"https://policies.google.com/privacy"}
                    target="_blank"
                  >
                    https://policies.google.com/privacy
                  </Link>
                  .
                </li>
                <li>
                  Slack, for our everyday communication and planning. Slack’s
                  privacy policy is here{" "}
                  <Link
                    to={
                      "https://slack.com/intl/en-gb/trust/privacy/privacy-policy"
                    }
                    target="_blank"
                  >
                    https://slack.com/intl/en-gb/trust/privacy/privacy-policy
                  </Link>
                  .
                </li>
              </ol>
              <ul>
                <li>
                  <strong>Legal requirements:</strong> We may disclose your
                  information if required by law, subpoena, or other legal
                  process, or if we believe it's necessary to protect our
                  rights, property, or safety, or the rights, property, or
                  safety of others.
                </li>
                <li>
                  <strong>Business transfers:</strong> In the event of a merger,
                  acquisition, or sale of all or a portion of our assets, your
                  information may be transferred as part of that transaction. We
                  will notify you of any such change of ownership or control of
                  your personal information.
                </li>
                <li>
                  <strong>Aggregated or anonymized data:</strong> We may share
                  aggregated or anonymized data that cannot be used to identify
                  you personally with third parties for research, marketing, or
                  other purposes.
                </li>
              </ul>
            </div>
            <div className="text-section">
              <h2>Your Choices and Rights</h2>
              <p>You have certain rights regarding your information:</p>
              <ul>
                <li>
                  <strong>Access and update:</strong> You can access and update
                  much of your personal information through your account
                  settings.
                </li>
                <li>
                  <strong>Opt-out of communications:</strong> You can opt out of
                  receiving promotional emails from us by following the
                  unsubscribe instructions in those emails.
                </li>
                <li>
                  <strong>Data deletion:</strong> You may have the right to
                  request the deletion of your personal data, subject to certain
                  legal exceptions.
                </li>
                <li>
                  <strong>Cookies:</strong> Most web browsers are set to accept
                  cookies by default. You can usually modify your browser
                  setting to decline cookies if you prefer. However, please note
                  that some features of our services may not function properly
                  if cookies are disabled.
                </li>
              </ul>
            </div>
            <div className="text-section">
              <h2>Data Security</h2>
              <p>
                We implement reasonable security measures to protect your
                information from unauthorized access, alteration, disclosure, or
                destruction. However, no internet transmission or electronic
                storage is completely secure, so we cannot guarantee absolute
                security.
              </p>
            </div>
            <div className="text-section">
              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or legal requirements. We will notify
                you of any significant changes by posting the new policy on our
                website and updating the "Last Updated" date. We encourage you
                to review this Privacy Policy periodically.
              </p>
            </div>
            <div className="text-section">
              <h2>Contact Us</h2>
              <p>
                If you have any questions or concerns about this Privacy Policy
                or our data practices, please contact us at
                support@backtoreality.com.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
